'use client'

import { Pagination } from '@/General/Pagination'
import {
  useQuickSearch,
  QuickSearchSortOption,
  QuickSearchFilterOption,
} from '@/QuickSearch/useQuickSearch'
import { useSearchParams } from 'next/navigation'
import { SearchResultsList } from '../SearchResultsList'
import { ITEMS_PER_PAGE } from '../constants'
import { FilterSelectorClient as FilterControl } from '../FilterControl'

export const SearchResultsClient = ({
  query,
  sort,
  filters,
  productTypes,
  currentPage,
}: SearchResultsClientProps) => {
  const searchParams = useSearchParams()

  // Ensure sort is a valid QuickSearchSortOption, default to RELEVANCE
  const validSort: QuickSearchSortOption = 
    (sort && ['RELEVANCE', 'PRICE_ASC', 'PRICE_DESC', 'DATE_ADDED_ASC', 'DATE_ADDED_DESC'].includes(sort))
      ? (sort as QuickSearchSortOption)
      : 'RELEVANCE'

  // Ensure filters are valid QuickSearchFilterOption values
  const validFilters: QuickSearchFilterOption[] = filters.filter(
    (f): f is QuickSearchFilterOption => 
      f === 'IN_STOCK' || f === 'ON_SALE'
  )

  const { results, isLoading, totalCount, facets } = useQuickSearch({
    query: query || '',
    sort: validSort,
    filters: validFilters,
    productTypes,
    pagination: {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
    },
  })

  return (
    <>
      <FilterControl
        facets={facets}
        isLoading={isLoading}
        initialFilters={filters}
        initialProductTypes={productTypes}
        query={query}
      />
      <SearchResultsList
        results={results}
        isLoading={isLoading}
        searchTerm={query}
      />
      {query && (
        <div className="pt-4">
          <Pagination
            page={currentPage}
            perPage={ITEMS_PER_PAGE}
            totalCount={totalCount}
            linkGenerator={page => {
              const params = new URLSearchParams(searchParams.toString())
              params.set('page', page.toString())
              if (query) params.set('query', query)
              if (sort) params.set('sort', sort)
              if (filters.length > 0) params.set('filter', filters.join(','))
              if (productTypes.length > 0) {
                params.set('productType', productTypes.join(','))
              }
              return `/search?${params.toString()}`
            }}
          />
        </div>
      )}
    </>
  )
}

interface SearchResultsClientProps {
  query: string
  sort: string | null
  filters: string[]
  productTypes: string[]
  currentPage: number
}
